// `/focus/meetings?project=<id>`: one project's
// meetings, grouped by day, and "Meeting starten" for that project.
//
// - With the feature off (`GET /api/meetings/status` → `enabled: false`) the
//   page is "not found", like any unknown URL.
// - A meeting is always a project's (owner decision). The page is
//   reached from the "Meetings" button on the project's Focus selection
//   screen, and the URL names the project. Without one it says "Wähle zuerst
//   ein Projekt" with the way back to the projects, and starts nothing.
// - Google is connected and disconnected on the account page only. Here,
//   while it is not connected, the page has one line linking there and no
//   start button.
// - "Meeting starten" launches for the page's project. The new tab is opened
//   INSIDE the click (`about:blank`, a popup blocker allows that), its
//   `opener` is cut, and only then does the server create the space; the tab
//   is pointed at the Meet link when the answer lands, and closed again when
//   the call fails. A blocked popup gets the link on the page instead.
// - "Aufnahme automatisch starten" (off by default, owner decision): off, the
//   space is made with auto-recording and auto-transcription OFF, so the host
//   tells the others first and starts both in Meet; on, Meet starts them as
//   soon as someone allowed to record joins. The last choice is remembered in
//   this browser only (a convenience; the server stores what each launch
//   used).
// - Each row says what its meeting is doing from server facts only (status,
//   recording flag, failure code, recording start: `meetingCopy.ts`), or,
//   once ready, its counts ("2 Tickets · 1 Epic · 9 nicht übernommen · 2
//   Punkte warten auf deine Antwort"). The page polls only while a row has
//   not settled (`useMeetings.ts`).
// - Meetings with no project yet (a Meet link reused from before every launch
//   named one) are listed under "Ohne Projekt", here and on the page without
//   a project; each opens its meeting page, where "Projekt wählen" sets one.
// - "Schon aufgenommen?": a meeting recorded without Loom is
//   processed for this project from the import page. The entry is there with
//   Google connected or not; the import page itself sends a disconnected
//   owner to the account page.

import { useEffect, useMemo, useState } from "react";

import type { MeetingListItemDto, RecordingStart } from "@loom/shared/meetings";

import { useFormatDate } from "../../i18n/format";
import { usePlural, useT, type MessageKey } from "../../i18n/index.js";
import { dayKeyOf, groupByDay } from "../../lib/group-by-day";
import { RouteNotFound } from "../../lib/RouteError";
import { AccountNavLink } from "../../account/AccountNavLink";
import { fetchProjects } from "../../projects/useProjects";
import { ThemeToggle } from "../../theme/ThemeToggle";
import { FocusFrame, Headline, Help, Kicker, ThinkingDots } from "../ui";

import { failureOf, launchMeeting } from "./meetingApi";
import { meetingsHref, meetingsImportHref } from "./MeetingsEntry";
import { failureKeyOf, phaseKeyOf } from "./meetingCopy";
import { useMeetingList, useMeetingsStatus, useUnassignedMeetings } from "./useMeetings";

const TIME_FORMAT: Intl.DateTimeFormatOptions = { timeStyle: "short" };
const DAY_FORMAT: Intl.DateTimeFormatOptions = { dateStyle: "medium" };
const DAY_TIME_FORMAT: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" };

/** A phone or tablet: Meet records only in a browser on a computer. */
const coarsePointer = (): boolean => {
  try {
    return typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
};

/** Where this browser keeps the owner's last recording-start choice. */
export const RECORDING_START_KEY = "loom.meetings.recordingStart";

/** The remembered choice; `manual` (the default) when none or unreadable. */
const readRecordingStart = (): RecordingStart => {
  try {
    return window.localStorage.getItem(RECORDING_START_KEY) === "auto" ? "auto" : "manual";
  } catch {
    return "manual";
  }
};

const keepRecordingStart = (start: RecordingStart): void => {
  try {
    window.localStorage.setItem(RECORDING_START_KEY, start);
  } catch {
    // Storage blocked: the toggle still works for this visit.
  }
};

/** A row's time: its first conference, else when Loom created it. */
const whenOf = (m: MeetingListItemDto): string => m.startedAt ?? m.createdAt;

/**
 * The day a row is listed under: `createdAt`, the field the server sorts the
 * list by, so a day is one run. (`statusSince` moves with every status, so a
 * retried old meeting would land under today amid older rows.)
 */
const dayOf = (m: MeetingListItemDto): string => m.createdAt;

function CountsLine({ m }: { readonly m: MeetingListItemDto }) {
  const t = useT();
  const tn = usePlural();
  const parts: string[] = [];
  if (m.counts.tickets > 0) parts.push(tn("plural.meetingTickets", m.counts.tickets));
  if (m.counts.epics > 0) parts.push(tn("plural.meetingEpics", m.counts.epics));
  if (m.counts.notTaken > 0) parts.push(tn("plural.meetingNotTaken", m.counts.notTaken));
  const total = m.counts.tickets + m.counts.epics;
  if (total > 0 && m.counts.filed < total) {
    parts.push(t("meetings.row.filedOf", { filed: m.counts.filed, total }));
  }
  // The items the doubt card still holds: the owner's answer is what they wait for.
  const waiting = m.counts.waiting ?? 0;
  if (waiting > 0) parts.push(tn("plural.meetingWaiting", waiting));
  if (parts.length === 0) return null;
  return <span className="meet-meta">{parts.join(" · ")}</span>;
}

function MeetingRow({
  m,
  onOpen,
}: {
  readonly m: MeetingListItemDto;
  readonly onOpen: () => void;
}) {
  const t = useT();
  const time = useFormatDate(TIME_FORMAT);
  const dayTime = useFormatDate(DAY_TIME_FORMAT);
  const phase = phaseKeyOf(m);
  // An imported meeting is listed under the day it was imported but shows
  // when it took place: then the time carries its own date.
  const when = whenOf(m);
  const whenLabel = dayKeyOf(when) === dayKeyOf(dayOf(m)) ? time(when) : dayTime(when);
  return (
    <li>
      <button type="button" className="meet-row is-button" onClick={onOpen}>
        <span className="meet-row-head">
          <span className="meet-meta">{whenLabel}</span>
          <span className="meet-row-title">{m.title ?? t("meetings.row.untitled")}</span>
        </span>
        <span className="meet-row-head">
          {phase !== null ? (
            <span className={`focus-chip meet-phase is-${m.status}`}>{t(phase)}</span>
          ) : (
            <CountsLine m={m} />
          )}
          {m.recordingFlag === "incomplete" && (
            <span className="focus-chip">{t("meetings.phase.incomplete")}</span>
          )}
          {m.spontaneous && <span className="focus-chip">{t("meetings.row.spontaneous")}</span>}
        </span>
      </button>
    </li>
  );
}

/**
 * The meetings with no project yet (from before every launch named one),
 * listed on every project's page and on the page without one, so the owner
 * can open each and pick its project there ("Projekt wählen"). Nothing when
 * there are none.
 */
function UnassignedMeetings({
  enabled,
  onOpenMeeting,
}: {
  readonly enabled: boolean;
  readonly onOpenMeeting: (meetingId: string) => void;
}) {
  const t = useT();
  const rows = useUnassignedMeetings(enabled);
  if (rows.length === 0) return null;
  return (
    <section className="meet-section">
      <h2 className="meet-section-title">{t("meetings.unassigned.title")}</h2>
      <Help>{t("meetings.unassigned.help")}</Help>
      <ul className="meet-list">
        {rows.map((m) => (
          <MeetingRow key={m.meetingId} m={m} onOpen={() => onOpenMeeting(m.meetingId)} />
        ))}
      </ul>
    </section>
  );
}

/**
 * The project's display name: the server's read on a row, else the recent
 * projects list; null when neither knows it (the page then names none).
 */
export const useProjectName = (
  enabled: boolean,
  projectId: number | null,
  rows: readonly MeetingListItemDto[] | null,
): string | null => {
  const [recentName, setRecentName] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled || projectId === null) return undefined;
    let alive = true;
    fetchProjects({ recent: true, includeGithub: true })
      .then((projects) => {
        if (alive) setRecentName(projects.find((p) => p.id === projectId)?.name ?? null);
      })
      .catch(() => {
        // The page does without the name.
      });
    return () => {
      alive = false;
    };
  }, [enabled, projectId]);
  const fromRows = rows?.find((m) => typeof m.projectName === "string")?.projectName ?? null;
  return fromRows ?? recentName;
};

export function MeetingsPage({
  projectId,
  onBack,
  onOpenMeeting,
  onOpenImport,
}: {
  /** The project the URL names; null without one (nothing to start then). */
  readonly projectId: number | null;
  /** To the project's Focus selection screen, or the projects without one. */
  readonly onBack: () => void;
  readonly onOpenMeeting: (meetingId: string) => void;
  /** To the import page; default a navigation by URL. */
  readonly onOpenImport?: (href: string) => void;
}) {
  const t = useT();
  const day = useFormatDate(DAY_FORMAT);
  const { status } = useMeetingsStatus();
  const enabled = status?.enabled === true;
  const list = useMeetingList(enabled, projectId);
  const projectName = useProjectName(enabled, projectId, list.meetings);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<MessageKey | null>(null);
  const [fallbackUri, setFallbackUri] = useState<string | null>(null);
  const [recordingStart, setRecordingStart] = useState<RecordingStart>(readRecordingStart);
  const phone = useMemo(coarsePointer, []);

  if (status === null) {
    return (
      <FocusFrame stepKey="meetings-loading" onBack={onBack}>
        <ThinkingDots label={t("common.loading")} />
      </FocusFrame>
    );
  }
  if (!enabled) return <RouteNotFound />;

  // Back from the account page returns to this project's meetings.
  const here = projectId === null ? undefined : meetingsHref(projectId);
  const chrome = (
    <>
      <AccountNavLink from={here} />
      <ThemeToggle />
    </>
  );

  if (projectId === null) {
    return (
      <FocusFrame stepKey="meetings-no-project" onBack={onBack} chromeAside={chrome}>
        <Kicker>{t("meetings.kicker")}</Kicker>
        <Headline>{t("meetings.noProject.headline")}</Headline>
        <div className="meet-actions">
          <button type="button" className="focus-btn" onClick={onBack}>
            {t("meetings.noProject.toProjects")}
          </button>
        </div>
        <UnassignedMeetings enabled={enabled} onOpenMeeting={onOpenMeeting} />
      </FocusFrame>
    );
  }

  const launch = (): void => {
    if (launching) return;
    setLaunchError(null);
    setFallbackUri(null);
    // Inside the click, before any await: a popup blocker lets this one through.
    const tab = window.open("about:blank", "_blank");
    if (tab !== null) {
      try {
        tab.opener = null;
      } catch {
        // A browser that refuses the write keeps its own opener policy.
      }
    }
    setLaunching(true);
    launchMeeting(projectId, recordingStart)
      .then((res) => {
        if (tab !== null && !tab.closed) tab.location.href = res.meetingUri;
        else setFallbackUri(res.meetingUri);
        list.reload();
      })
      .catch((err: unknown) => {
        tab?.close();
        setLaunchError(failureKeyOf(failureOf(err)));
      })
      .finally(() => setLaunching(false));
  };

  const todayKey = dayKeyOf(new Date().toISOString());
  const yesterdayKey = dayKeyOf(new Date(Date.now() - 86_400_000).toISOString());
  const dayHeading = (key: string): string =>
    key === todayKey
      ? t("meetings.list.today")
      : key === yesterdayKey
        ? t("meetings.list.yesterday")
        : day(`${key}T12:00:00`);

  const connected = status.googleConnected === true;
  const groups = groupByDay(list.meetings ?? [], dayOf);

  return (
    <FocusFrame stepKey="meetings" onBack={onBack} wide chromeAside={chrome}>
      <Kicker>{t("meetings.kicker")}</Kicker>
      <Headline>{t("meetings.headline")}</Headline>
      {projectName !== null && (
        <p className="meet-meta meet-scope">{t("meetings.project.current", { project: projectName })}</p>
      )}

      {connected ? (
        <div className="meet-start">
          <div className="meet-actions">
            <button type="button" className="focus-btn" disabled={launching} onClick={launch}>
              {t("meetings.start.plain")}
            </button>
          </div>
          <label className="meet-toggle">
            <input
              type="checkbox"
              checked={recordingStart === "auto"}
              disabled={launching}
              onChange={(e) => {
                const next: RecordingStart = e.target.checked ? "auto" : "manual";
                setRecordingStart(next);
                keepRecordingStart(next);
              }}
            />
            {t("meetings.start.autoRecord")}
          </label>
          {recordingStart === "manual" && <Help>{t("meetings.start.manualHint")}</Help>}
          {launching && <ThinkingDots label={t("meetings.start.starting")} />}
          {phone && <Help>{t("meetings.start.phoneNote")}</Help>}
          {fallbackUri !== null && (
            <p className="focus-help">
              {t("meetings.start.popupBlocked")}{" "}
              <a href={fallbackUri} target="_blank" rel="noopener noreferrer">
                {t("meetings.start.openMeet")}
              </a>
            </p>
          )}
          {launchError !== null && (
            <div className="focus-error" role="alert">
              {t(launchError)}
            </div>
          )}
        </div>
      ) : (
        <p className="focus-help meet-start">
          <AccountNavLink label="meetings.google.connectInAccount" from={here} />
        </p>
      )}

      <div className="meet-start meet-import-entry">
        <p className="meet-confirm-title">{t("meetings.import.entryTitle")}</p>
        <div className="meet-actions">
          <button
            type="button"
            className="focus-btn is-ghost"
            onClick={() => {
              const href = meetingsImportHref(projectId);
              if (onOpenImport) onOpenImport(href);
              else window.location.assign(href);
            }}
          >
            {t("meetings.import.entryButton")}
          </button>
        </div>
      </div>

      {list.meetings === null && !list.failed && <ThinkingDots label={t("common.loading")} />}
      {list.failed && (
        <div className="focus-error" role="alert">
          {t("meetings.list.loadFailed")}{" "}
          <button type="button" className="focus-deeplink" onClick={list.reload}>
            {t("common.retry")}
          </button>
        </div>
      )}
      {list.meetings !== null && list.meetings.length === 0 && (
        <Help>{t("meetings.list.empty")}</Help>
      )}
      {groups.map((g) => (
        <section key={g.key} className="meet-section">
          <h2 className="meet-section-title">{dayHeading(g.key)}</h2>
          <ul className="meet-list">
            {g.entries.map((m) => (
              <MeetingRow key={m.meetingId} m={m} onOpen={() => onOpenMeeting(m.meetingId)} />
            ))}
          </ul>
        </section>
      ))}
      {list.hasMore && (
        <button
          type="button"
          className="focus-btn is-ghost"
          disabled={list.loadingMore}
          onClick={list.loadMore}
        >
          {t("meetings.list.more")}
        </button>
      )}
      <UnassignedMeetings enabled={enabled} onOpenMeeting={onOpenMeeting} />
    </FocusFrame>
  );
}
